import { useImageFileDrop } from './useImageFileDrop';
import { Fragment, useState } from 'react';
import { AdminAdvertiserInput, type AdminAdvertiser } from '@lub0v/santa-utils';
import { showDialog, closeDialog } from '@src/components/Dialog/Dialog';
import { Button } from '@src/components/Button/Button';
import { santaClient } from '@src/state/client';

export function editAdvertiser(
  ru: boolean,
  advertiser: AdminAdvertiser | undefined,
  onSaved: (item: AdminAdvertiser) => void,
  canEditExternalId: boolean,
) {
  showDialog({
    title: advertiser
      ? ru
        ? 'Рекламодатель'
        : 'Advertiser'
      : ru
        ? 'Новый рекламодатель'
        : 'New advertiser',
    closeLabel: ru ? 'Закрыть' : 'Close',
    children: (
      <AdvertiserEditor
        ru={ru}
        advertiser={advertiser}
        onSaved={onSaved}
        canEditExternalId={canEditExternalId}
      />
    ),
  });
}

function AdvertiserEditor({
  ru,
  advertiser,
  onSaved,
  canEditExternalId,
}: {
  ru: boolean;
  advertiser?: AdminAdvertiser;
  onSaved: (item: AdminAdvertiser) => void;
  canEditExternalId: boolean;
}) {
  const [draft, setDraft] = useState(() => {
    const value = new AdminAdvertiserInput();
    if (advertiser) {
      for (const key of Object.keys(value) as (keyof AdminAdvertiserInput)[]) {
        Object.assign(value, { [key]: advertiser[key] ?? value[key] });
      }
    }
    return value;
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fields = [
    ['storeBrandName', 'Store brand name', 'Название бренда / магазина', 256],
    ['storeLogoUrl', 'Logo URL', 'Ссылка на логотип', 4096],
    ['advertiserLegalName', 'Legal name', 'Юридическое наименование', 2000],
    ['advertiserTaxId', 'Tax ID', 'ИНН', 64],
    ['advertiserExternalId', 'External ID', 'Внешний ID', 128],
    ['advertiserInfo', 'Advertiser information', 'Информация о рекламодателе', 10000],
  ] as const;
  async function uploadLogo(file?: File) {
    if (!file || busy || uploading) return;
    if (
      !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) ||
      !file.size ||
      file.size > 2 * 1024 * 1024
    ) {
      setError(
        ru
          ? 'Выберите JPG, PNG, WebP или GIF размером до 2 МБ.'
          : 'Choose a JPG, PNG, WebP or GIF up to 2 MB.',
      );
      return;
    }
    setUploading(true);
    setError('');
    try {
      const result = await santaClient.uploadAdminAdvertiserLogo(file, file.name, file.type);
      if (!result.ok || !result.data) throw new Error();
      setDraft((old) => ({ ...old, storeLogoUrl: result.data!.publicUrl }));
    } catch {
      setError(
        ru
          ? 'Не удалось загрузить логотип. Попробуйте ещё раз.'
          : 'Could not upload the logo. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  }
  const { isFileOver, dropHandlers } = useImageFileDrop(busy || uploading, (file) => {
    void uploadLogo(file);
  });
  async function save() {
    if (busy || uploading || !draft.storeBrandName.trim()) return;
    setBusy(true);
    setError('');
    try {
      const result = advertiser
        ? await santaClient.updateAdminAdvertiser({ ...draft, id: String(advertiser.id) })
        : await santaClient.createAdminAdvertiser(draft);
      if (!result.ok || !result.data) throw new Error();
      onSaved(result.data);
      closeDialog();
    } catch {
      setError(
        ru
          ? 'Не удалось сохранить. Проверьте поля и повторите.'
          : 'Could not save. Check the fields and try again.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="admin-advertiser-editor">
      <fieldset disabled={busy || uploading}>
        {fields.map(([key, en, russian, max]) => (
          <Fragment key={key}>
            <label>
              <span>
                {ru ? russian : en}
                {key === 'storeBrandName' && (
                  <span className="admin-goods__required" aria-hidden="true">
                    {' '}
                    *
                  </span>
                )}
              </span>
              {key === 'advertiserInfo' ? (
                <textarea
                  rows={4}
                  maxLength={max}
                  value={draft[key]}
                  onChange={(e) => setDraft((old) => ({ ...old, [key]: e.target.value }))}
                />
              ) : (
                <input
                  readOnly={key === 'advertiserExternalId' && !canEditExternalId}
                  required={key === 'storeBrandName'}
                  type={key === 'storeLogoUrl' ? 'url' : 'text'}
                  maxLength={max}
                  value={draft[key]}
                  onChange={(e) => setDraft((old) => ({ ...old, [key]: e.target.value }))}
                />
              )}
            </label>
            {key === 'storeLogoUrl' && (
              <div
                className={`admin-advertiser-editor__logo${isFileOver ? ' is-file-over' : ''}`}
                {...dropHandlers}
              >
                {draft.storeLogoUrl && (
                  <img
                    draggable={false}
                    src={draft.storeLogoUrl}
                    alt={ru ? 'Логотип рекламодателя' : 'Advertiser logo'}
                  />
                )}
                <label>
                  {ru
                    ? 'Загрузите или перетащите логотип сюда (до 2 МБ)'
                    : 'Upload or drop a logo here (up to 2 MB)'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      void uploadLogo(file);
                    }}
                  />
                </label>
              </div>
            )}
          </Fragment>
        ))}
      </fieldset>
      {uploading && <p role="status">{ru ? 'Загрузка логотипа…' : 'Uploading logo…'}</p>}
      {error && <p role="alert">{error}</p>}
      <div className="admin-goods__actions">
        <Button variant="ghost" disabled={busy || uploading} onClick={closeDialog}>
          {ru ? 'Отмена' : 'Cancel'}
        </Button>
        <Button
          variant="primary"
          loading={busy}
          disabled={uploading || !draft.storeBrandName.trim()}
          onClick={() => void save()}
        >
          {ru ? 'Сохранить' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
